import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Grid,
  Typography,
  MenuItem,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  Checkbox,
  ListItemText,
  FormControlLabel,
  FormGroup,
  Box,
  Alert,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent
} from '@mui/material';
import { CheckCircle, PersonAdd } from '@mui/icons-material';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const HREmployeeForm = ({ seatStatus, refreshSeatStatus }) => {
  const [formData, setFormData] = useState({
    employee_id: '',
    firstName: '',
    lastName: '',
    personalemail: '',
    companyemail: '',
    employmentStatus: '',
    department: '',
    designation: '',
    joiningDate: '',
    roleType: '',
    reportingManager: '',
    teamSize: '',
    shiftType: '',
    loginTime: '',
    logoutTime: '',
    selectedResponsibilities: [],
    additionalResponsibilities: '',
    costCenterIds: []
  });

  const [companyInfo, setCompanyInfo] = useState({ emailGenMode: 'automated', emailDomain: '', registration_no: null });
  const [emailSuggestions, setEmailSuggestions] = useState([]);
  const [emailError, setEmailError] = useState('');
  const [firstNameError, setFirstNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [empIdError, setEmpIdError] = useState('');
  const [personalEmailError, setPersonalEmailError] = useState('');

  const [costCenters, setCostCenters] = useState([]);

  useEffect(() => {
    const fetchCostCenters = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;
        const response = await fetch(`${API_URL}/api/cost-centers`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const result = await response.json();
        if (result.success) {
          setCostCenters(result.data || []);
        }
      } catch (err) {
        //console.error("Error fetching cost centers:", err);
      }
    };
    fetchCostCenters();
  }, []);

  const [companyId, setCompanyId] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [customDepartment, setCustomDepartment] = useState('');
  const [customRoleType, setCustomRoleType] = useState('');

  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const payload = JSON.parse(atob(token.split('.')[1]));
        const uId = payload.id;
        const userRes = await fetch(`${API_URL}/api/cmp/users/${uId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const userData = await userRes.json();
        const cId = userData.data?.company_id;
        if (cId) {
          setCompanyId(cId);
          const cmpRes = await fetch(`${API_URL}/api/cmp/${cId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const cmpData = await cmpRes.json();
          if (cmpData.data) {
            setCompanyInfo({
              emailGenMode: cmpData.data.emailGenMode || 'automated',
              emailDomain: cmpData.data.emailDomain || '',
              registration_no: cmpData.data.registration_no
            });
          }
        }
      } catch (err) {
        //console.error("Error fetching company details:", err);
      }
    };
    fetchCompanyData();
  }, []);

  useEffect(() => {
    const f = formData.firstName.trim().toLowerCase();
    const l = formData.lastName.trim().toLowerCase();
    if (companyInfo.emailGenMode === 'automated' && companyInfo.emailDomain && f) {
      const domain = companyInfo.emailDomain.toLowerCase();
      let suggestions = [];
      if (l) {
        suggestions.push(`${f}.${l}@${domain}`);
        suggestions.push(`${f}${l}@${domain}`);
      } else {
        suggestions.push(`${f}@${domain}`);
      }
      setEmailSuggestions(suggestions);
    } else {
      setEmailSuggestions([]);
    }
  }, [formData.firstName, formData.lastName, companyInfo]);

  const validateFirstName = (name) => {
    const trimmed = (name || '').trim();
    if (!trimmed) {
      setFirstNameError('First name is required.');
      return false;
    }
    const nameRegex = /^[a-zA-Z\s.'-]+$/;
    if (!nameRegex.test(trimmed)) {
      setFirstNameError('First name must contain only letters and spaces (no numbers).');
      return false;
    }
    setFirstNameError('');
    return true;
  };

  const validateLastName = (name) => {
    const trimmed = (name || '').trim();
    if (!trimmed) {
      setLastNameError('Last name is required.');
      return false;
    }
    const nameRegex = /^[a-zA-Z\s.'-]+$/;
    if (!nameRegex.test(trimmed)) {
      setLastNameError('Last name must contain only letters and spaces (no numbers).');
      return false;
    }
    setLastNameError('');
    return true;
  };

  const checkEmpIdUniqueness = async (idToCheck) => {
    const trimmed = (idToCheck || '').trim();
    if (!trimmed) {
      setEmpIdError('');
      return true;
    }

    const idRegex = /^[A-Za-z0-9_-]{3,20}$/;
    if (!idRegex.test(trimmed)) {
      setEmpIdError('Employee ID must be 3-20 alphanumeric characters without spaces (hyphens and underscores allowed).');
      return false;
    }

    try {
      const token = localStorage.getItem('token');
      const targetCmpId = companyInfo.registration_no || companyId;
      const res = await fetch(`${API_URL}/api/admin/employees/check-employee-id?employee_id=${encodeURIComponent(trimmed)}&company_id=${targetCmpId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.exists) {
        setEmpIdError('Employee ID is already in use in this company.');
        return false;
      } else {
        setEmpIdError('');
        return true;
      }
    } catch {
      return true;
    }
  };

  const checkPersonalEmailUniqueness = async (emailToCheck, compEmail) => {
    const trimmed = (emailToCheck || '').trim().toLowerCase();
    if (!trimmed) {
      setPersonalEmailError('Personal email is required.');
      return false;
    }

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(trimmed)) {
      setPersonalEmailError('Please enter a valid personal email address (e.g., user@example.com).');
      return false;
    }

    const cmp = (compEmail !== undefined ? compEmail : formData.companyemail || '').trim().toLowerCase();
    if (cmp && trimmed === cmp) {
      setPersonalEmailError('Personal email cannot be identical to company email.');
      return false;
    }

    try {
      const token = localStorage.getItem('token');
      const targetCmpId = companyInfo.registration_no || companyId;
      const res = await fetch(`${API_URL}/api/admin/employees/check-personal-email?email=${encodeURIComponent(trimmed)}&company_id=${targetCmpId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.exists) {
        setPersonalEmailError('This personal email is already registered in this company.');
        return false;
      } else {
        setPersonalEmailError('');
        return true;
      }
    } catch {
      return true;
    }
  };

  const checkEmailUniqueness = async (emailToCheck, persEmail) => {
    const trimmed = (emailToCheck || '').trim().toLowerCase();
    if (!trimmed) {
      if (companyInfo.emailGenMode === 'manual') {
        setEmailError('Company email is required.');
        return false;
      }
      setEmailError('');
      return true;
    }

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(trimmed)) {
      setEmailError('Invalid company email format.');
      return false;
    }

    const domain = (companyInfo.emailDomain || '').toLowerCase();
    if (domain && !trimmed.endsWith(`@${domain}`)) {
      setEmailError(`Company email must end with the registered domain: @${domain}`);
      return false;
    }

    const pers = (persEmail !== undefined ? persEmail : formData.personalemail || '').trim().toLowerCase();
    if (pers && trimmed === pers) {
      setEmailError('Company email cannot be identical to personal email.');
      return false;
    }

    try {
      const token = localStorage.getItem('token');
      const targetCmpId = companyInfo.registration_no || companyId;
      const res = await fetch(`${API_URL}/api/admin/employees/check-email?email=${encodeURIComponent(trimmed)}&company_id=${targetCmpId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.exists) {
        setEmailError('This company email is already registered for this company.');
        return false;
      } else {
        setEmailError('');
        return true;
      }
    } catch {
      return true;
    }
  };

  const roleTypes = [
    "CEO", "CTO", "Board of directors", "Section manager/Department manger", 
    "Manager", "Project Manager", "Product Manager", "Product Owner", "Finance Manager", "HR", "HR Manager", "Hiring Manager", "Priciple engineer/architect", 
    "Team Lead", "Senior Software Engineer", "Software Engineer", 
    "Junior Software Engineer", "Intern"
  ];

  const employmentStatusOptions = [
    "Active", "Deceased","Resigned"
  ];

  const departmentOptions = [
    "Engineering", "Human Resources", "Sales", "Marketing", 
    "Finance", "Operations", "Product", "Customer Support"
  ];

  const shiftTypeOptions = [
    "General Shift", "Early Shift", "Evening Shift", "Night Shift", "Hybrid Shift","Remote Shift"
  ];

  const responsibilities = {
    "ceo": ["Strategic planning", "Business development", "Executive leadership", "Stakeholder relations", "Company vision"],
    "cto": ["Technology strategy", "Technical leadership", "Innovation management", "IT governance", "Technical risk assessment"],
    "board of directors": ["Corporate governance", "Strategic oversight", "CEO supervision", "Financial accountability", "Shareholder representation"],
    "section manager/department manger": ["Departmental leadership", "Budget management", "Cross-team coordination", "Department KPI tracking", "Process improvement"],
    "manager": ["Team management", "Project oversight", "Performance evaluation", "Resource allocation", "Department strategy"],
    "hr manager": ["Employee relations", "HR policy development", "Benefits administration", "Employee engagement", "Talent development"],
    "hiring manager": ["Candidate screening", "Interview coordination", "Recruitment strategy", "Onboarding oversight", "Hiring decisions"],
    "priciple engineer/architect": ["System architecture design", "Technical decision-making", "Engineering standards", "Technical mentorship", "Technology roadmap"],
    "team lead": ["Team coordination", "Technical guidance", "Sprint planning", "Progress reporting", "Code reviews"],
    "senior software engineer": ["Complex feature implementation", "Architecture participation", "Technical mentoring", "Advanced problem solving", "Code quality enforcement"],
    "software engineer": ["Software development", "Problem solving", "Code testing", "Documentation", "Feature implementation"],
    "junior software engineer": ["Basic feature implementation", "Bug fixing", "Learning codebase", "Pair programming", "Testing and QA"],
    "intern": ["Learning", "Assigned tasks", "Progress reporting", "Skill development", "Support activities"]
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleResponsibilityChange = (responsibility) => {
    setFormData(prev => {
      const current = prev.selectedResponsibilities;
      if (current.includes(responsibility)) {
        return {
          ...prev,
          selectedResponsibilities: current.filter(r => r !== responsibility)
        };
      } else {
        return {
          ...prev,
          selectedResponsibilities: [...current, responsibility]
        };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isFnValid = validateFirstName(formData.firstName);
    const isLnValid = validateLastName(formData.lastName);
    const isEmpIdValid = await checkEmpIdUniqueness(formData.employee_id);
    const isPersonalValid = await checkPersonalEmailUniqueness(formData.personalemail, formData.companyemail);
    const isCompValid = await checkEmailUniqueness(formData.companyemail, formData.personalemail);

    if (!isFnValid || !isLnValid || !isEmpIdValid || !isPersonalValid || !isCompValid || firstNameError || lastNameError || empIdError || personalEmailError || emailError) {
      setError("Please fix all highlighted errors in the form before submitting.");
      return;
    }

    if (seatStatus?.isExpired) {
      setError("Subscription/Trial has expired. Please upgrade to add employees.");
      return;
    }

    if (seatStatus?.isLimitReached) {
      setError(`User seat limit reached (${seatStatus?.usedSeats}/${seatStatus?.totalSeats}). Please upgrade your plan to add more employees.`);
      return;
    }

    setIsSubmitting(true);
    setError('');

    const finalFormData = {
      ...formData,
      department: formData.department === 'Other' ? customDepartment.trim() : formData.department,
      roleType: formData.roleType === 'Other' ? customRoleType.trim() : formData.roleType
    };

    try {
       const token = localStorage.getItem("token"); // get JWT token
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
      const response = await fetch(`${API_URL}/api/alldetails`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(finalFormData)
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSubmittedData({
          employeeId: result.data.employee_id,
          companyEmail: result.data.companyemail,
          name: `${result.data.firstName} ${result.data.lastName}`
        });
        setSubmitSuccess(true);
        if (refreshSeatStatus) refreshSeatStatus();
      } else {
        setError(result.message || 'Failed to add employee');
        if (refreshSeatStatus) refreshSeatStatus();
      }
    } catch (err) {
      setError('An error occurred while submitting the form');
      //console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseDialog = () => {
    setSubmitSuccess(false);
    setCustomDepartment('');
    setCustomRoleType('');
    setFormData({
      employee_id: '',
      firstName: '',
      lastName: '',
      personalemail: '',
      companyemail: '',
      employmentStatus: '',
      department: '',
      designation: '',
      joiningDate: '',
      roleType: '',
      reportingManager: '',
      teamSize: '',
      shiftType: '',
      loginTime: '',
      logoutTime: '',
      selectedResponsibilities: [],
      additionalResponsibilities: '',
      costCenterIds: []
    });
  };

  const availableResponsibilities = formData.roleType 
    ? responsibilities[formData.roleType.toLowerCase()] || []
    : [];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          
          <Typography variant="h5" >
            Add New Employee
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Personal Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom color="primary">
                Personal Information
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Employee ID (Optional)"
                name="employee_id"
                value={formData.employee_id}
                onChange={(e) => {
                  handleInputChange(e);
                  checkEmpIdUniqueness(e.target.value);
                }}
                onBlur={(e) => checkEmpIdUniqueness(e.target.value)}
                error={!!empIdError}
                variant="outlined"
                helperText={empIdError || "Leave empty to auto-generate Employee ID, or type a custom one (3-20 alphanumeric characters)."}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={(e) => {
                  handleInputChange(e);
                  validateFirstName(e.target.value);
                }}
                onBlur={(e) => validateFirstName(e.target.value)}
                error={!!firstNameError}
                helperText={firstNameError || ""}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={(e) => {
                  handleInputChange(e);
                  validateLastName(e.target.value);
                }}
                onBlur={(e) => validateLastName(e.target.value)}
                error={!!lastNameError}
                helperText={lastNameError || ""}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                type="email"
                label="Personal Email"
                name="personalemail"
                value={formData.personalemail}
                onChange={(e) => {
                  handleInputChange(e);
                  checkPersonalEmailUniqueness(e.target.value, formData.companyemail);
                }}
                onBlur={(e) => checkPersonalEmailUniqueness(e.target.value, formData.companyemail)}
                error={!!personalEmailError}
                helperText={personalEmailError || "Enter employee's personal email address (e.g., user@example.com)."}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                required={companyInfo.emailGenMode === 'manual'}
                type="email"
                label="Company Email"
                name="companyemail"
                value={formData.companyemail}
                onChange={(e) => {
                  handleInputChange(e);
                  checkEmailUniqueness(e.target.value, formData.personalemail);
                }}
                onBlur={(e) => checkEmailUniqueness(e.target.value, formData.personalemail)}
                error={!!emailError}
                helperText={
                  emailError || (companyInfo.emailGenMode === 'automated'
                    ? "Suggestions will appear as you type first and last name. You can click a suggestion or edit manually."
                    : "Enter employee's official company email address.")
                }
                variant="outlined"
              />
              {companyInfo.emailGenMode === 'automated' && emailSuggestions.length > 0 && (
                <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold' }}>Suggestions:</Typography>
                  {emailSuggestions.map((suggestion) => (
                    <Chip
                      key={suggestion}
                      label={suggestion}
                      clickable
                      color={formData.companyemail === suggestion ? "primary" : "default"}
                      onClick={() => {
                        setFormData(prev => ({ ...prev, companyemail: suggestion }));
                        checkEmailUniqueness(suggestion);
                      }}
                      size="small"
                    />
                  ))}
                </Box>
              )}
            </Grid>

            {/* Employment Details */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom color="primary">
                Employment Details
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Employment Status</InputLabel>
                <Select
                  name="employmentStatus"
                  value={formData.employmentStatus}
                  onChange={handleInputChange}
                  label="Employment Status"
                >
                  {employmentStatusOptions.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Department</InputLabel>
                <Select
                  name="department"
                  value={formData.department}
                  onChange={handleInputChange}
                  label="Department"
                >
                  {departmentOptions.map((dept) => (
                    <MenuItem key={dept} value={dept}>
                      {dept}
                    </MenuItem>
                  ))}
                  <MenuItem value="Other">Other (Custom Department)</MenuItem>
                </Select>
              </FormControl>
              {formData.department === 'Other' && (
                <TextField
                  fullWidth
                  required
                  label="Custom Department"
                  value={customDepartment}
                  onChange={(e) => setCustomDepartment(e.target.value)}
                  variant="outlined"
                  sx={{ mt: 2 }}
                />
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="cost-center-label">Cost Centers</InputLabel>
                <Select
                  labelId="cost-center-label"
                  name="costCenterIds"
                  multiple
                  value={formData.costCenterIds || []}
                  onChange={handleInputChange}
                  label="Cost Centers"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const cc = costCenters.find(c => c.id === value);
                        return cc ? `${cc.name} (${cc.code})` : value;
                      }).join(', ')}
                    </Box>
                  )}
                >
                  {costCenters.map((cc) => (
                    <MenuItem key={cc.id} value={cc.id}>
                      <Checkbox checked={(formData.costCenterIds || []).indexOf(cc.id) > -1} />
                      <ListItemText primary={`${cc.name} (${cc.code})`} />
                    </MenuItem>
                  ))}
                </Select>
             
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Designation"
                name="designation"
                value={formData.designation}
                onChange={handleInputChange}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="date"
                label="Joining Date"
                name="joiningDate"
                value={formData.joiningDate}
                onChange={handleInputChange}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Shift Details */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom color="primary">
                Shift Details
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Shift Type</InputLabel>
                <Select
                  name="shiftType"
                  value={formData.shiftType}
                  onChange={handleInputChange}
                  label="Shift Type"
                >
                  <MenuItem value="">Select Shift Type</MenuItem>
                  {shiftTypeOptions.map((shift) => (
                    <MenuItem key={shift} value={shift}>
                      {shift}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="time"
                label="Login Time"
                name="loginTime"
                value={formData.loginTime}
                onChange={handleInputChange}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="time"
                label="Logout Time"
                name="logoutTime"
                value={formData.logoutTime}
                onChange={handleInputChange}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Role Information */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom color="primary">
                Role Information
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Role Type</InputLabel>
                <Select
                  name="roleType"
                  value={formData.roleType}
                  onChange={handleInputChange}
                  label="Role Type"
                >
                  {roleTypes.map((role) => (
                    <MenuItem key={role} value={role}>
                      {role}
                    </MenuItem>
                  ))}
                  <MenuItem value="Other">Other (Custom Role Type)</MenuItem>
                </Select>
              </FormControl>
              {formData.roleType === 'Other' && (
                <TextField
                  fullWidth
                  required
                  label="Custom Role Type"
                  value={customRoleType}
                  onChange={(e) => setCustomRoleType(e.target.value)}
                  variant="outlined"
                  sx={{ mt: 2 }}
                />
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Reporting Manager"
                name="reportingManager"
                value={formData.reportingManager}
                onChange={handleInputChange}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Team Size"
                name="teamSize"
                value={formData.teamSize}
                onChange={handleInputChange}
                variant="outlined"
                inputProps={{ min: 0 }}
              />
            </Grid>

            {/* Responsibilities */}
            {availableResponsibilities.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                  Select Responsibilities
                </Typography>
                <FormGroup row>
                  {availableResponsibilities.map((resp) => (
                    <Grid item xs={12} sm={6} md={4} key={resp}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.selectedResponsibilities.includes(resp)}
                            onChange={() => handleResponsibilityChange(resp)}
                          />
                        }
                        label={resp}
                      />
                    </Grid>
                  ))}
                </FormGroup>
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Additional Responsibilities"
                name="additionalResponsibilities"
                value={formData.additionalResponsibilities}
                onChange={handleInputChange}
                variant="outlined"
                placeholder="Enter any additional responsibilities..."
              />
            </Grid>

            {/* Submit Button */}
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting || seatStatus?.isLimitReached || seatStatus?.isExpired}
                sx={{ mt: 2, py: 1.5, justifyContent: 'center', minWidth: '200px', ml: 'auto' }}
              >
                {isSubmitting 
                  ? 'Submitting...' 
                  : (seatStatus?.isLimitReached 
                      ? 'Seat Limit Reached' 
                      : (seatStatus?.isExpired ? 'Trial Expired' : 'Add Employee'))}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Success Dialog */}
      <Dialog
        open={submitSuccess}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', color: 'success.main' }}>
            <CheckCircle sx={{ mr: 1, fontSize: 32 }} />
            <Typography variant="h5">Employee Added Successfully!</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {submittedData && (
            <Card variant="outlined" sx={{ mt: 2 }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Employee Name
                    </Typography>
                    <Typography variant="h6">
                      {submittedData.name}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Employee ID
                    </Typography>
                    <Chip 
                      label={submittedData.employeeId} 
                      color="primary" 
                      sx={{ mt: 0.5, fontWeight: 'bold' }}
                    />
                  </Grid>
                 
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Company Email
                    </Typography>
                    <Typography variant="body1">
                      {submittedData.companyEmail}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} variant="contained" color="primary">
            Add Another Employee
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default HREmployeeForm;